import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import { PermissionsEnum } from '@/auth/interfaces';
import { hasPermission } from '@/shared/utils';
import {
  useGuardarMfaPolitica,
  useMfaPolitica,
} from '@/shared/actions/erp/administration/mfa-admin';

export const MfaAdminPoliticaTab = () => {
  const puedeEditar = hasPermission(
    PermissionsEnum.api_gestionar_politica_mfa
  );

  const { data, isLoading } = useMfaPolitica();
  const guardar = useGuardarMfaPolitica();

  const [obligatorioGlobal, setObligatorioGlobal] = useState(false);
  const [diasGrace, setDiasGrace] = useState<number>(7);
  const [gruposSeleccionados, setGruposSeleccionados] = useState<
    { id: number; name: string }[]
  >([]);

  useEffect(() => {
    if (!data) return;
    setObligatorioGlobal(data.obligatorio_global);
    setDiasGrace(data.dias_grace);
    const seleccion = data.grupos_disponibles.filter((g) =>
      (data.grupos_obligatorios || []).includes(g.id)
    );
    setGruposSeleccionados(seleccion);
  }, [data]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data) {
    return <Alert severity="error">No se pudo cargar la política.</Alert>;
  }

  const onGuardar = async () => {
    try {
      await guardar.mutateAsync({
        obligatorio_global: obligatorioGlobal,
        dias_grace: diasGrace,
        grupos_obligatorios: gruposSeleccionados.map((g) => g.id),
      });
      toast.success('Política actualizada');
    } catch {
      /* interceptor */
    }
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        La política global se aplica a todos los usuarios <strong>no</strong> de
        integración. Cuando un usuario afectado entre por primera vez sin tener
        2FA configurado, se le otorgará automáticamente el periodo de gracia
        definido para que pueda configurarlo.
      </Alert>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={obligatorioGlobal}
                onChange={(e) => setObligatorioGlobal(e.target.checked)}
                disabled={!puedeEditar}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle2">
                  2FA obligatorio para todos
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Aplica a todos los usuarios humanos del sistema.
                </Typography>
              </Box>
            }
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Grupos obligatorios
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Los miembros de estos grupos deben tener 2FA configurado.
            </Typography>
            <Autocomplete
              multiple
              size="small"
              disabled={!puedeEditar}
              options={data.grupos_disponibles}
              value={gruposSeleccionados}
              onChange={(_, v) => setGruposSeleccionados(v)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    size="small"
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Seleccionar grupos..." />
              )}
            />
          </Box>

          <TextField
            label="Días del período de gracia"
            type="number"
            size="small"
            sx={{ maxWidth: 240 }}
            value={diasGrace}
            onChange={(e) => setDiasGrace(parseInt(e.target.value || '0', 10))}
            disabled={!puedeEditar}
            inputProps={{ min: 0, max: 365 }}
            helperText="0 = sin gracia (bloqueo inmediato si no tiene 2FA)"
          />

          <Typography variant="caption" color="text.secondary">
            Última modificación:{' '}
            {data.fecha_modificacion
              ? new Date(data.fecha_modificacion).toLocaleString()
              : '-'}
            {data.actualizado_por ? ` por ${data.actualizado_por}` : ''}
          </Typography>

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={!puedeEditar || guardar.isPending}
              onClick={onGuardar}
            >
              Guardar política
            </Button>
          </Box>
        </Stack>
      </Paper>

      {!puedeEditar && (
        <Alert severity="warning">
          Solo lectura — necesitas el permiso{' '}
          <code>api.gestionar_politica_mfa</code> para modificar la política.
        </Alert>
      )}
    </Stack>
  );
};
